import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../lib/useAuth';

const navClass = ({ isActive }: { isActive: boolean }) =>
  `text-sm font-medium tracking-wide transition-colors ${
    isActive ? 'text-brand-600' : 'text-slate-600 hover:text-brand-600'
  }`;

export function PublicLayout() {
  const { status, user, isAdmin, logout } = useAuth();

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-slate-100 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="inline-block h-8 w-8 rounded-full bg-brand-500" aria-hidden />
            <span className="font-serif text-xl tracking-tight text-ink-900">SecretsOverflow</span>
          </Link>

          <nav className="flex items-center gap-6">
            <NavLink to="/" className={navClass}>
              Search
            </NavLink>
            {isAdmin && (
              <NavLink to="/admin" className={navClass}>
                Admin
              </NavLink>
            )}
            {status === 'authenticated' ? (
              <div className="flex items-center gap-4">
                <span className="hidden text-sm text-slate-500 sm:inline">{user?.email}</span>
                <button type="button" onClick={() => void logout()} className="btn-outline-brand">
                  Sign out
                </button>
              </div>
            ) : (
              <Link to="/admin/login" className="btn-outline-brand">
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-navy-900 text-slate-300">
        <div className="mx-auto max-w-6xl px-4 py-12">
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="font-serif text-lg text-white">SecretsOverflow</span>
            <p className="max-w-lg text-sm text-slate-400">
              An overflow booking service from{' '}
              <a href="https://www.secretsfl.com" className="text-brand-500 hover:text-brand-200">
                Secrets Hideaway Resort
              </a>
              . When the resort is fully booked, find a nearby room so you can still join us for the
              event.
            </p>
          </div>

          <div className="mt-8 flex flex-col items-center gap-2 text-sm text-slate-400">
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              <span>
                TEXT or CALL{' '}
                <a href="tel:+18445636969" className="text-slate-200 hover:text-brand-500">
                  844.563.6969
                </a>
              </span>
              <span className="text-slate-600">·</span>
              <a href="mailto:info@secretsfl.com" className="text-slate-200 hover:text-brand-500">
                info@secretsfl.com
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
              <span>2145 E Irlo Bronson Memorial Hwy, Kissimmee, FL 34744</span>
              <span className="text-slate-600">·</span>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=2145+E+Irlo+Bronson+Memorial+Hwy,+Kissimmee,+FL+34744"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-200 hover:text-brand-500"
              >
                Get Directions
              </a>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center gap-1 border-t border-white/10 pt-6 text-xs text-slate-500">
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              <span>© {new Date().getFullYear()} Secrets Hideaway</span>
              <span className="text-slate-700">·</span>
              <a href="https://www.secretsfl.com/terms-privacy" className="hover:text-brand-500">
                Terms &amp; Privacy
              </a>
              <span className="text-slate-700">·</span>
              <a href="https://www.secretsfl.com/gdpr-privacy-policy" className="hover:text-brand-500">
                GDPR Privacy Policy
              </a>
              <span className="text-slate-700">·</span>
              <a href="https://www.secretsfl.com/accessibility" className="hover:text-brand-500">
                Accessibility
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
